import { z } from 'zod'

import { ParentSchema } from '~/@entities/Parent'
import { GradeSchema, SchoolTypeSchema } from '~/@entities/SchoolSystem'

/***************** 재원 상태 *****************/
export const STUDENT_STATUS = ['ACTIVE', 'INACTIVE', 'DELETED'] as const

/***************** 학생앱 허용 서비스 *****************/
// 학생앱에서 쓸 수 있게 허용된 서비스. 앱이 떠 있는 브랜드(Brand)와는 다른 서버 어휘다.
export const SERVICE_TYPE = ['MATHFLAT', 'ENGLISHFLAT', 'SCIENCEFLAT'] as const

export const StudentStatusSchema = z.enum(STUDENT_STATUS)
export type StudentStatus = z.infer<typeof StudentStatusSchema>

export const ServiceTypeSchema = z.enum(SERVICE_TYPE)
export type ServiceType = z.infer<typeof ServiceTypeSchema>

// 학생 고유 ID(`I726693`)와 로그인 ID(`S68199589`). legacy 는 템플릿 리터럴 브랜드였지만 캐스트 24곳이
// 보여주듯 컴파일 타임에 강제되지 않았다. 접두 검사를 런타임으로 옮기고 타입은 string 으로 둔다.
export const StudentIdSchema = z.string().regex(/^I/, '학생 ID 는 I 로 시작한다')
export type StudentId = z.infer<typeof StudentIdSchema>
export const StudentLoginIdSchema = z.string().regex(/^S/, '학생 로그인 ID 는 S 로 시작한다')

const _SiblingSchema = z.object({
  id: StudentIdSchema,
  name: z.string(),
  schoolType: SchoolTypeSchema,
  grade: GradeSchema,
})

// /students/{studentId} 응답. 필수·선택은 스펙(StudentDetailResponse)을 따른다.
// 스펙에 있는 studentLoginId·parentLoginId·student(중첩 DTO)는 서버 생성자 인자일 뿐 직렬화되지 않아 받지 않는다.
export const StudentSchema = z.object({
  id: StudentIdSchema,
  loginId: StudentLoginIdSchema,
  name: z.string(),
  schoolType: SchoolTypeSchema,
  grade: GradeSchema,
  status: StudentStatusSchema,
  attendanceCode: z.string(),
  schoolName: z.string(),
  email: z.string(),
  address: z.string(),
  phone: z.string(),
  homePhone: z.string(),
  memo: z.string().nullable().optional(),
  birthDate: z.string().nullable().optional(),
  // 재원 시작일(yyyy-MM-dd). 입력하지 않은 학생은 null 이다.
  startDate: z.string().nullable(),
  allowedServiceTypes: z.array(ServiceTypeSchema),
  // 학원 전체가 수정 제한이어도 이 학생의 이 항목만은 고칠 수 있다는 예외 목록.
  // restriction 과 같은 dot-notation 이라 나중에 한쪽으로 합칠 수 있다.
  // 생성 스펙(StudentDetailResponse)에 아직 없는 필드라 없을 때를 빈 배열로 둔다.
  restrictionExemptions: z.array(z.string()).default([]),
  parent: ParentSchema.optional(),
  siblings: z.array(_SiblingSchema),
})
export type Student = z.infer<typeof StudentSchema>

/***************** 학생 목록 *****************/
// /students(쪽 나눔)·/students/list 응답(StudentResponse). 서버의 상세 응답이 이 응답을 상속하므로 상세 스키마에서 공통 필드를 고른다.
// 보호자는 미등록이어도 빈 문자열로 채운 객체로 온다. 서버는 보호자 email 도 싣지만 목록에서는 읽지 않아 받지 않는다.
// 2026-10-14 백엔드 develop(6a0f8e7bb) 의 student/controller/response/StudentResponse.kt 로 확인.
export const StudentSummarySchema = StudentSchema.pick({
  id: true,
  loginId: true,
  name: true,
  schoolType: true,
  grade: true,
  status: true,
  phone: true,
  attendanceCode: true,
  memo: true,
  allowedServiceTypes: true,
}).extend({ parent: ParentSchema.omit({ email: true }) })
export type StudentSummary = z.infer<typeof StudentSummarySchema>

/***************** 학교급·학년별 학생 묶음 *****************/
// /students/* 가 내는 StudentGroupResponse. 멤버는 엔드포인트마다 다른 하위 클래스라 묶음마다 멤버를 extend 한다.
export const StudentGroupMemberSchema = z.object({
  id: StudentIdSchema,
  name: StudentSchema.shape.name,
})
export const StudentGroupSchema = z.object({
  schoolType: SchoolTypeSchema,
  grade: GradeSchema,
  memberList: z.array(StudentGroupMemberSchema),
})

// /students/phone 응답(StudentMemberWithPhoneNumberResponse). 학생·보호자 전화번호는 없으면 null 이다.
export const StudentPhoneGroupSchema = StudentGroupSchema.extend({
  memberList: z.array(
    StudentGroupMemberSchema.extend({
      phone: StudentSchema.shape.phone.nullable(),
      parent: z.object({ phone: ParentSchema.shape.phone.nullable() }),
    }),
  ),
})
export type StudentPhoneGroup = z.infer<typeof StudentPhoneGroupSchema>

/***************** 엑셀 일괄 등록 *****************/
// 성공 응답. 실패하면 서버는 성공 응답 대신 오류 본문(code STUDENT_EXCEL_FAILURE)의 data 에 실패 행을 싣는다.
export const StudentExcelResultSchema = z.object({
  success: z.boolean(),
  successRows: z.array(z.number()),
  failedRows: z.array(z.number()),
})
export type StudentExcelResult = z.infer<typeof StudentExcelResultSchema>

// 실패 행. 행 번호는 사람이 보는 1부터의 번호이고, 파일 형식 오류처럼 특정 행이 아닌 실패는 null 이다.
export const StudentExcelFailureRowSchema = z.object({
  rowNumber: z.number().nullable(),
  message: z.string(),
})
export type StudentExcelFailureRow = z.infer<typeof StudentExcelFailureRowSchema>

// 학생앱 설정. 서버는 영문 플래그명으로 내주지만 앱의 공용어는 한글이라(채점 뷰옵션 로직·테스트 275곳)
// adapter 가 이름을 바꿔 이 모양으로 만든다. /students/app-setting/{studentId} 가 내주는 값이며
// 학생 상세와는 다른 엔드포인트라 Student 에 넣지 않는다.
export const StudentAppSettingSchema = z.object({
  studentId: StudentIdSchema,
  일반채점_채점전정답공개: z.boolean(),
  채점후정답해설공개: z.boolean(),
  채점전문풀동공개: z.boolean(),
  채점후문풀동공개: z.boolean(),
  학부모정답해설공개: z.boolean(),
  lectureService: z.boolean(),
  개념힌트_사용: z.boolean(),
  풀이분석_사용: z.boolean(),
  풀이확인_사용: z.boolean(),
  availableDailyWorksheet: z.boolean(),
})
export type StudentAppSetting = z.infer<typeof StudentAppSettingSchema>
